import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import useMotionTrackingEntitlement, { clearMotionTrackingSession } from "../hooks/useMotionTrackingEntitlement";
// /home/ubuntu/avaai_dashboard/frontend/src/components/MobileOrbMenu.tsx
import { useContext, useEffect, useRef, useState } from "react";
import { ThemeContext } from "../context/ThemeContext";
import { usePreferences, } from "../context/PreferencesContext";
import { getAvaaioUiCopy, } from "../utils/avaaioUiCopy";
import "../styles/MobileOrbMenu.css";
const THEME_ORDER = [
    "cyan",
    "purple",
    "emerald",
    "amber",
    "lava",
    "midnight",
    "aurora",
];
function emit(name) {
    window.dispatchEvent(new CustomEvent(name));
}
/*
 * AVAAIO MOBILE UX PHASE 3A
 *
 * V2 intentionally establishes the new lower-right home position
 * without inheriting an obsolete V1 coordinate from the previous
 * mobile layout.
 */
const LS_ORB_POS = "ava_mobile_orb_position_v2";
const ORB_SIZE = 78;
const ORB_MARGIN = 14;
const ORB_DOCK_GAP = 14;
const ORB_COLLISION_GAP = 14;
const ORB_CLEAR_DELAY_MS = 450;
let safeAreaCacheKey = "";
let safeAreaCache = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
};
function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}
function numericCssValue(value) {
    const parsed = Number.parseFloat(value || "0");
    return Number.isFinite(parsed) ? parsed : 0;
}
function getSafeAreaInsets() {
    if (typeof window === "undefined" ||
        typeof document === "undefined" ||
        !document.body) {
        return safeAreaCache;
    }
    const cacheKey = `${window.innerWidth}x${window.innerHeight}`;
    if (cacheKey === safeAreaCacheKey) {
        return safeAreaCache;
    }
    const probe = document.createElement("div");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText = [
        "position:fixed",
        "visibility:hidden",
        "pointer-events:none",
        "width:0",
        "height:0",
        "padding-top:env(safe-area-inset-top)",
        "padding-right:env(safe-area-inset-right)",
        "padding-bottom:env(safe-area-inset-bottom)",
        "padding-left:env(safe-area-inset-left)",
    ].join(";");
    document.body.appendChild(probe);
    const computed = window.getComputedStyle(probe);
    safeAreaCache = {
        top: numericCssValue(computed.paddingTop),
        right: numericCssValue(computed.paddingRight),
        bottom: numericCssValue(computed.paddingBottom),
        left: numericCssValue(computed.paddingLeft),
    };
    safeAreaCacheKey = cacheKey;
    probe.remove();
    return safeAreaCache;
}
function getOrbViewportBounds() {
    if (typeof window === "undefined") {
        return {
            left: 0,
            top: 0,
            right: 390,
            bottom: 844,
            width: 390,
            height: 844,
        };
    }
    const visualViewport = window.visualViewport;
    const left = visualViewport?.offsetLeft || 0;
    const top = visualViewport?.offsetTop || 0;
    const width = visualViewport?.width || window.innerWidth;
    const height = visualViewport?.height || window.innerHeight;
    return {
        left,
        top,
        right: left + width,
        bottom: top + height,
        width,
        height,
    };
}
function hasSavedOrbPosition() {
    try {
        return localStorage.getItem(LS_ORB_POS) !== null;
    }
    catch {
        return false;
    }
}
function getVisibleChatDockTop(bounds) {
    if (typeof document === "undefined") {
        return null;
    }
    /*
     * AVAAIO MOBILE UX PHASE 4F
     *
     * `.chat-dock` itself covers the complete viewport, so it
     * must never be treated as the visible bottom control.
     * Inspect its visible children and the real holo console.
     */
    const elements = Array.from(document.querySelectorAll(".holo-console, .chat-dock > *"));
    const candidateTops = elements
        .map(element => {
        const style = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();
        const visible = style.display !== "none" &&
            style.visibility !== "hidden" &&
            numericCssValue(style.opacity) !== 0 &&
            rect.width > 1 &&
            rect.height > 1 &&
            rect.bottom > bounds.top &&
            rect.top < bounds.bottom;
        if (!visible) {
            return null;
        }
        /*
         * Exclude full-screen overlay containers. A genuine
         * dock/control occupies the lower portion of the view.
         */
        const lowerScreenControl = rect.top >
            bounds.top +
                Math.min(130, bounds.height * 0.30) &&
            rect.bottom >
                bounds.top +
                    bounds.height * 0.52 &&
            rect.height <
                bounds.height * 0.72;
        return lowerScreenControl
            ? rect.top
            : null;
    })
        .filter((value) => typeof value === "number" &&
        Number.isFinite(value));
    if (candidateTops.length === 0) {
        return null;
    }
    return Math.min(...candidateTops);
}
function normalizePos(raw) {
    if (typeof window === "undefined") {
        return raw;
    }
    const bounds = getOrbViewportBounds();
    const safeArea = getSafeAreaInsets();
    const minX = bounds.left +
        Math.max(ORB_MARGIN, safeArea.left + 6);
    const maxX = bounds.right -
        ORB_SIZE -
        Math.max(ORB_MARGIN, safeArea.right + 6);
    const minY = bounds.top +
        Math.max(ORB_MARGIN, safeArea.top + 6);
    const viewportMaxY = bounds.bottom -
        ORB_SIZE -
        Math.max(ORB_MARGIN, safeArea.bottom + 6);
    const dockTop = getVisibleChatDockTop(bounds);
    const dockMaxY = dockTop !== null
        ? dockTop -
            ORB_SIZE -
            ORB_DOCK_GAP
        : viewportMaxY;
    const maxY = Math.min(viewportMaxY, dockMaxY);
    return {
        x: clamp(raw.x, minX, Math.max(minX, maxX)),
        y: clamp(raw.y, minY, Math.max(minY, maxY)),
    };
}
function getDefaultPos() {
    if (typeof window === "undefined") {
        return { x: 18, y: 18 };
    }
    const bounds = getOrbViewportBounds();
    const safeArea = getSafeAreaInsets();
    const rightMargin = Math.max(ORB_MARGIN, safeArea.right + 6);
    const dockTop = getVisibleChatDockTop(bounds);
    const isPortrait = bounds.height >= bounds.width;
    const fallbackBottomClearance = isPortrait ? 138 : 108;
    const preferredY = dockTop !== null
        ? dockTop - ORB_SIZE - ORB_DOCK_GAP
        : bounds.bottom -
            ORB_SIZE -
            fallbackBottomClearance;
    return normalizePos({
        x: bounds.right -
            ORB_SIZE -
            rightMargin,
        y: preferredY,
    });
}
function getOrbRect(pos) {
    return {
        left: pos.x,
        top: pos.y,
        right: pos.x + ORB_SIZE,
        bottom: pos.y + ORB_SIZE,
    };
}
function rectsOverlap(first, second, gap = 0) {
    return (first.left < second.right + gap &&
        first.right > second.left - gap &&
        first.top < second.bottom + gap &&
        first.bottom > second.top - gap);
}
/*
 * AVAAIO MOBILE UX PHASE 4F
 *
 * Collision obstacles that can occupy useful orb space.
 * Regular users see the live Vision preview. Admin users may
 * additionally see the protected diagnostic HUD.
 */
function getVisibleOrbObstacles() {
    if (typeof document === "undefined") {
        return [];
    }
    const bounds = getOrbViewportBounds();
    const selectors = [
        ".ava-vision-live-preview",
        ".ava-vision-user-hud",
        ".ava-vision-admin-hud",
        ".ava-browser-panel",
        /*
         * AVAAIO SPLASH PROTECTION:
         * Protect the entire visible login/splash card.
         * This keeps the floating orb clear of AVAAIO branding,
         * Meet Ava wording, generation copy, authentication
         * controls, and important notices.
         */
        ".mint-login-card",
    ];
    const seen = new Set();
    const visible = [];
    for (const selector of selectors) {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
            if (seen.has(element)) {
                return;
            }
            seen.add(element);
            const style = window.getComputedStyle(element);
            const rect = element.getBoundingClientRect();
            const onScreen = rect.right > bounds.left &&
                rect.left < bounds.right &&
                rect.bottom > bounds.top &&
                rect.top < bounds.bottom;
            /*
             * Ignore invisible and full-screen layout containers.
             * Only actual cards/panels should displace the orb.
             */
            const genuineObstacle = style.display !== "none" &&
                style.visibility !== "hidden" &&
                numericCssValue(style.opacity) !== 0 &&
                rect.width > 1 &&
                rect.height > 1 &&
                onScreen &&
                (rect.width <
                    bounds.width * 0.96 ||
                    rect.height <
                        bounds.height * 0.96);
            if (genuineObstacle) {
                visible.push(element);
            }
        });
    }
    return visible;
}
function getVisibleOrbObstacleRects() {
    return getVisibleOrbObstacles()
        .map(element => {
        const rect = element.getBoundingClientRect();
        return {
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
        };
    });
}
function orbPositionIsBlocked(position, obstacles) {
    const orbRect = getOrbRect(position);
    return obstacles.some(obstacle => rectsOverlap(orbRect, obstacle, ORB_COLLISION_GAP));
}
function orbPositionDistance(first, second) {
    const dx = first.x - second.x;
    const dy = first.y - second.y;
    return (dx * dx +
        dy * dy);
}
/*
 * Choose the nearest valid position instead of always forcing
 * the orb to the opposite side. This minimizes distracting
 * movement on phones, tablets, foldables, and resized windows.
 */
function getNearestSafeOrbPos(home, obstacles) {
    const preferred = normalizePos(home);
    if (!orbPositionIsBlocked(preferred, obstacles)) {
        return preferred;
    }
    const bounds = getOrbViewportBounds();
    const safeArea = getSafeAreaInsets();
    const leftX = bounds.left +
        Math.max(ORB_MARGIN, safeArea.left + 6);
    const rightX = bounds.right -
        ORB_SIZE -
        Math.max(ORB_MARGIN, safeArea.right + 6);
    const topY = bounds.top +
        Math.max(ORB_MARGIN, safeArea.top + 6);
    const bottomY = normalizePos({
        x: preferred.x,
        y: bounds.bottom,
    }).y;
    const candidates = [];
    const candidateKeys = new Set();
    const addCandidate = (raw) => {
        const position = normalizePos(raw);
        const key = `${Math.round(position.x)}:` +
            `${Math.round(position.y)}`;
        if (candidateKeys.has(key)) {
            return;
        }
        candidateKeys.add(key);
        candidates.push(position);
    };
    addCandidate(preferred);
    addCandidate(getDefaultPos());
    addCandidate({
        x: leftX,
        y: preferred.y,
    });
    addCandidate({
        x: rightX,
        y: preferred.y,
    });
    addCandidate({
        x: leftX,
        y: topY,
    });
    addCandidate({
        x: rightX,
        y: topY,
    });
    addCandidate({
        x: leftX,
        y: bottomY,
    });
    addCandidate({
        x: rightX,
        y: bottomY,
    });
    /*
     * Generate positions beside and above/below every visible
     * obstacle. These candidates work regardless of whether a
     * device is portrait, landscape, split-screen, or folded.
     */
    for (const obstacle of obstacles) {
        const obstacleCenterX = (obstacle.left +
            obstacle.right) / 2;
        const obstacleCenterY = (obstacle.top +
            obstacle.bottom) / 2;
        const besideLeft = obstacle.left -
            ORB_SIZE -
            ORB_COLLISION_GAP;
        const besideRight = obstacle.right +
            ORB_COLLISION_GAP;
        const above = obstacle.top -
            ORB_SIZE -
            ORB_COLLISION_GAP;
        const below = obstacle.bottom +
            ORB_COLLISION_GAP;
        addCandidate({
            x: besideLeft,
            y: preferred.y,
        });
        addCandidate({
            x: besideRight,
            y: preferred.y,
        });
        addCandidate({
            x: obstacleCenterX -
                ORB_SIZE / 2,
            y: above,
        });
        addCandidate({
            x: obstacleCenterX -
                ORB_SIZE / 2,
            y: below,
        });
        addCandidate({
            x: besideLeft,
            y: obstacleCenterY -
                ORB_SIZE / 2,
        });
        addCandidate({
            x: besideRight,
            y: obstacleCenterY -
                ORB_SIZE / 2,
        });
        addCandidate({
            x: besideLeft,
            y: above,
        });
        addCandidate({
            x: besideRight,
            y: above,
        });
        addCandidate({
            x: besideLeft,
            y: below,
        });
        addCandidate({
            x: besideRight,
            y: below,
        });
    }
    const validCandidates = candidates
        .filter(position => !orbPositionIsBlocked(position, obstacles))
        .sort((first, second) => orbPositionDistance(first, preferred) -
        orbPositionDistance(second, preferred));
    if (validCandidates.length > 0) {
        return validCandidates[0];
    }
    /*
     * Narrow split-screen and foldable fallback. Search the
     * available safe region on a coarse grid only when the
     * normal nearby candidates are all occupied.
     */
    const searchStep = Math.max(28, Math.round(ORB_SIZE * 0.48));
    const gridCandidates = [];
    for (let y = topY; y <= bottomY; y += searchStep) {
        for (let x = leftX; x <= rightX; x += searchStep) {
            const position = normalizePos({
                x,
                y,
            });
            if (!orbPositionIsBlocked(position, obstacles)) {
                gridCandidates.push(position);
            }
        }
    }
    gridCandidates.sort((first, second) => orbPositionDistance(first, preferred) -
        orbPositionDistance(second, preferred));
    return (gridCandidates[0] ||
        preferred);
}
function loadSavedPos() {
    try {
        const raw = localStorage.getItem(LS_ORB_POS);
        if (!raw)
            return getDefaultPos();
        const parsed = JSON.parse(raw);
        if (typeof parsed?.x === "number" && typeof parsed?.y === "number") {
            return normalizePos(parsed);
        }
    }
    catch { }
    return getDefaultPos();
}
function savePos(pos) {
    try {
        localStorage.setItem(LS_ORB_POS, JSON.stringify(pos));
    }
    catch { }
}
function themeLabel(theme) {
    switch (theme) {
        case "cyan":
            return "Cyan";
        case "purple":
            return "Purple";
        case "emerald":
            return "Emerald";
        case "amber":
            return "Amber";
        case "lava":
            return "Lava";
        case "midnight":
            return "Midnight";
        case "aurora":
            return "Aurora";
        default:
            return theme;
    }
}
export default function MobileOrbMenu() {
    const [orbEyeOpen, setOrbEyeOpen] = useState(() => {
        try {
            // Ava Vision must always start OFF.
            // Never restore previous camera/eye state automatically.
            return false;
        }
        catch {
            return false;
        }
    });
    useEffect(() => {
        const syncEye = (event) => {
            const custom = event;
            const next = !!custom?.detail?.open;
            setOrbEyeOpen(next);
            try {
                localStorage.setItem("ava_eye_open", next ? "1" : "0");
            }
            catch { }
        };
        window.addEventListener("ava-eye-toggle", syncEye);
        return () => window.removeEventListener("ava-eye-toggle", syncEye);
    }, []);
    const toggleOrbEye = () => {
        const next = !orbEyeOpen;
        setOrbEyeOpen(next);
        try {
            localStorage.setItem("ava_eye_open", next ? "1" : "0");
        }
        catch { }
        window.dispatchEvent(new Event("ava_interaction"));
        window.dispatchEvent(new CustomEvent("ava-eye-toggle", {
            detail: { open: next, source: "orb-menu", ts: Date.now() },
        }));
        window.dispatchEvent(new Event("ava_face_cam_tracking_changed"));
    };
    // =====================================================
    // AVA MOTION TRACK
    //
    // MobileOrbMenu owns only the displayed control state.
    // ChatBox owns permission handling and activation.
    // AvaFace owns the actual motion tracking.
    // =====================================================
    const { motionTrackingAllowed, } = useMotionTrackingEntitlement();
    const [orbMotionEnabled, setOrbMotionEnabled] = useState(false);
    useEffect(() => {
        if (motionTrackingAllowed)
            return;
        clearMotionTrackingSession();
        setOrbMotionEnabled(false);
    }, [motionTrackingAllowed]);
    useEffect(() => {
        const syncMotionTrack = (event) => {
            const custom = event;
            const requested = Boolean(custom?.detail?.enabled);
            const next = requested && motionTrackingAllowed;
            if (requested && !motionTrackingAllowed) {
                console.warn("[AVA MOTION] Orb state rejected by entitlement.");
                clearMotionTrackingSession();
            }
            setOrbMotionEnabled(next);
            try {
                sessionStorage.setItem("ava_device_motion_look", next ? "1" : "0");
            }
            catch { }
        };
        window.addEventListener("ava-device-motion-toggle", syncMotionTrack);
        return () => {
            window.removeEventListener("ava-device-motion-toggle", syncMotionTrack);
        };
    }, [motionTrackingAllowed]);
    const toggleOrbMotionTrack = async () => {
        window.dispatchEvent(new Event("ava_interaction"));
        const nextEnabled = !orbMotionEnabled;
        if (nextEnabled && !motionTrackingAllowed) {
            clearMotionTrackingSession();
            setOrbMotionEnabled(false);
            window.alert(interfaceCopy.mobile.motionPaidAlert);
            return;
        }
        // Apple requires this request to happen directly inside
        // the original button click / transient user activation.
        if (nextEnabled) {
            const OrientationEvent = window.DeviceOrientationEvent;
            if (!OrientationEvent) {
                window.alert("Ava Motion Track is not available in this browser or app view.");
                return;
            }
            let permissionGranted = false;
            try {
                permissionGranted =
                    sessionStorage.getItem("ava_device_motion_permission_granted") === "1";
            }
            catch { }
            if (typeof OrientationEvent.requestPermission ===
                "function" &&
                !permissionGranted) {
                try {
                    const permission = await OrientationEvent.requestPermission();
                    if (permission !== "granted") {
                        setOrbMotionEnabled(false);
                        try {
                            sessionStorage.setItem("ava_device_motion_look", "0");
                        }
                        catch { }
                        window.alert("Ava Motion Track remains off. Allow Motion & Orientation Access, then try again.");
                        return;
                    }
                    try {
                        sessionStorage.setItem("ava_device_motion_permission_granted", "1");
                    }
                    catch { }
                }
                catch (error) {
                    console.warn("[AVA MOTION] Direct permission request failed", error);
                    setOrbMotionEnabled(false);
                    window.alert("Ava could not open the Motion & Orientation permission request.");
                    return;
                }
            }
        }
        window.dispatchEvent(new CustomEvent("ava-mobile-motion-toggle", {
            detail: {
                enabled: nextEnabled,
                permissionGranted: nextEnabled,
                source: "orb-menu",
                ts: Date.now(),
            },
        }));
    };
    const [menuChatPlan, setMenuChatPlan] = useState(() => {
        try {
            return localStorage.getItem("ava_chat_plan") || "free";
        }
        catch {
            return "free";
        }
    });
    const [menuChatLock, setMenuChatLock] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("ava_chat_lock") || "{}");
        }
        catch {
            return {};
        }
    });
    useEffect(() => {
        try {
            const rawPlan = String(localStorage.getItem("ava_chat_plan") || "free")
                .trim()
                .toLowerCase();
            const rawRole = String(localStorage.getItem("ava_user_role") || "user")
                .trim()
                .toLowerCase();
            const sub = String(localStorage.getItem("subscription_status") || "inactive")
                .trim()
                .toLowerCase();
            const isAdmin = rawRole === "admin";
            const isPro = sub === "active" ||
                rawPlan === "pro" ||
                rawRole === "vip";
            const sanitized = isAdmin
                ? "admin"
                : isPro
                    ? "pro"
                    : "free";
            setMenuChatPlan(sanitized);
            localStorage.setItem("ava_chat_plan", sanitized);
            console.log("[AvaVision] sanitized plan =", sanitized);
        }
        catch (e) {
            console.warn("[AvaVision] sanitizer failed:", e);
        }
    }, []);
    useEffect(() => {
        const syncLock = (event) => {
            try {
                const custom = event;
                if (custom?.detail?.plan) {
                    setMenuChatPlan(String(custom.detail.plan || "free"));
                }
                else {
                    setMenuChatPlan(localStorage.getItem("ava_chat_plan") || "free");
                }
                if (typeof custom?.detail?.lock !== "undefined") {
                    setMenuChatLock(custom.detail.lock || {});
                }
                else {
                    setMenuChatLock(JSON.parse(localStorage.getItem("ava_chat_lock") || "{}"));
                }
            }
            catch {
                try {
                    setMenuChatPlan(localStorage.getItem("ava_chat_plan") || "free");
                    setMenuChatLock(JSON.parse(localStorage.getItem("ava_chat_lock") || "{}"));
                }
                catch { }
            }
        };
        window.addEventListener("ava_chat_lock_changed", syncLock);
        window.addEventListener("storage", syncLock);
        return () => {
            window.removeEventListener("ava_chat_lock_changed", syncLock);
            window.removeEventListener("storage", syncLock);
        };
    }, []);
    const isFreePlanUser = String(menuChatPlan || "").toLowerCase() === "free";
    const isLockedFreeUser = isFreePlanUser &&
        Boolean(menuChatLock?.limit_reached);
    const viewportW = typeof window !== "undefined" ? window.innerWidth : 390;
    const viewportH = typeof window !== "undefined" ? window.innerHeight : 844;
    const isLandscape = viewportW > viewportH;
    const isPhone = viewportW <= 640;
    const isCompactLandscape = isLandscape &&
        viewportH <= 500;
    const goProfile = () => {
        window.location.href = "/profile";
        closeMenu();
    };
    const runQuickCommand = (cmd) => {
        try {
            window.dispatchEvent(new CustomEvent("ava-orb-command", {
                detail: { command: cmd },
            }));
        }
        catch { }
        closeMenu();
    };
    const goUpgrade = () => {
        window.location.href = "/settings";
        closeMenu();
    };
    const lockNotice = () => {
        window.dispatchEvent(new Event("ava_interaction"));
        try {
            alert("This control is locked on the Free plan. Upgrade to Pro to unlock more Ava controls.");
        }
        catch { }
    };
    // === LIVE THEME SYSTEM ===
    const { theme, setTheme } = useContext(ThemeContext);
    const { preferences } = usePreferences();
    const interfaceCopy = getAvaaioUiCopy(preferences.language);
    const [open, setOpen] = useState(false);
    const [themeOpen, setThemeOpen] = useState(false);
    // =========================================
    // REACTIVE ORB STATES
    // =========================================
    const [orbSpeaking, setOrbSpeaking] = useState(false);
    const [orbThinking, setOrbThinking] = useState(false);
    const [orbListening, setOrbListening] = useState(false);
    const [pos, setPos] = useState(() => loadSavedPos());
    const posRef = useRef(pos);
    // AVAAIO MOBILE UX PHASE 3A:
    // preferredPosRef is the user's home position. Automatic Vision
    // avoidance changes the rendered position without overwriting home.
    const preferredPosRef = useRef(pos);
    const userPositionedRef = useRef(hasSavedOrbPosition());
    const autoRelocatedRef = useRef(false);
    const visionClearTimerRef = useRef(null);
    const dragRef = useRef({
        active: false,
        moved: false,
        startX: 0,
        startY: 0,
        offsetX: 0,
        offsetY: 0,
    });
    useEffect(() => {
        posRef.current = pos;
    }, [pos]);
    useEffect(() => {
        if (!open)
            return;
        const timer = setTimeout(() => {
            setOpen(false);
            setThemeOpen(false);
        }, 9000);
        return () => clearTimeout(timer);
    }, [open, themeOpen]);
    useEffect(() => {
        let resizeRaf = 0;
        const onResize = () => {
            cancelAnimationFrame(resizeRaf);
            resizeRaf = requestAnimationFrame(() => {
                if (dragRef.current.active)
                    return;
                const preferred = userPositionedRef.current
                    ? normalizePos(preferredPosRef.current)
                    : getDefaultPos();
                preferredPosRef.current = preferred;
                if (userPositionedRef.current) {
                    savePos(preferred);
                }
                if (!autoRelocatedRef.current) {
                    posRef.current = preferred;
                    setPos(preferred);
                }
            });
        };
        onResize();
        window.addEventListener("resize", onResize);
        window.addEventListener("orientationchange", onResize);
        window.visualViewport?.addEventListener("resize", onResize);
        window.visualViewport?.addEventListener("scroll", onResize);
        return () => {
            cancelAnimationFrame(resizeRaf);
            window.removeEventListener("resize", onResize);
            window.removeEventListener("orientationchange", onResize);
            window.visualViewport?.removeEventListener("resize", onResize);
            window.visualViewport?.removeEventListener("scroll", onResize);
        };
    }, []);
    /*
     * AVAAIO MOBILE UX PHASE 4F
     *
     * All-device responsive orb collision authority.
     *
     * - Preserves the user's preferred/saved position.
     * - Moves only when the preferred position is blocked.
     * - Chooses the nearest safe position.
     * - Avoids Vision preview, admin HUD, browser panels,
     *   chat controls, safe areas, display cutouts, and keyboard.
     * - Re-evaluates after rotation, resize, viewport movement,
     *   DOM changes, and measured obstacle-size changes.
     */
    useEffect(() => {
        let evaluationRaf = 0;
        let evaluationInterval = 0;
        let resizeObserver = null;
        const clearReturnTimer = () => {
            if (visionClearTimerRef.current !==
                null) {
                window.clearTimeout(visionClearTimerRef.current);
                visionClearTimerRef.current =
                    null;
            }
        };
        const applyPosition = (next) => {
            const current = posRef.current;
            /*
             * Avoid unnecessary React updates when viewport geometry
             * changes by less than one physical CSS pixel.
             */
            if (Math.abs(current.x - next.x) < 1 &&
                Math.abs(current.y - next.y) < 1) {
                return;
            }
            posRef.current = next;
            setPos(next);
        };
        const getPreferred = () => {
            const preferred = userPositionedRef.current
                ? normalizePos(preferredPosRef.current)
                : getDefaultPos();
            preferredPosRef.current =
                preferred;
            return preferred;
        };
        const preferredAreaIsBlocked = (preferred, obstacles) => orbPositionIsBlocked(preferred, obstacles);
        const scheduleReturnHome = () => {
            if (visionClearTimerRef.current !==
                null) {
                return;
            }
            visionClearTimerRef.current =
                window.setTimeout(() => {
                    visionClearTimerRef.current =
                        null;
                    if (dragRef.current.active) {
                        return;
                    }
                    const preferred = getPreferred();
                    const obstacles = getVisibleOrbObstacleRects();
                    if (preferredAreaIsBlocked(preferred, obstacles)) {
                        return;
                    }
                    autoRelocatedRef.current =
                        false;
                    applyPosition(preferred);
                }, ORB_CLEAR_DELAY_MS);
        };
        const evaluateLayout = () => {
            if (dragRef.current.active) {
                return;
            }
            /*
             * AVAAIO_ORB_MANUAL_POSITION_PRIORITY_V2
             *
             * The user's saved position remains the permanent orb
             * home position. Automatic collision avoidance may move
             * only the rendered orb while protected content occupies
             * that location.
             *
             * Automatic relocation never overwrites the saved home.
             */
            if (userPositionedRef.current) {
                clearReturnTimer();
                const userPreferred = getPreferred();
                const userObstacles = getVisibleOrbObstacleRects();
                const userHomeBlocked = preferredAreaIsBlocked(userPreferred, userObstacles);
                if (userHomeBlocked) {
                    autoRelocatedRef.current =
                        true;
                    applyPosition(getNearestSafeOrbPos(userPreferred, userObstacles));
                }
                else {
                    autoRelocatedRef.current =
                        false;
                    applyPosition(userPreferred);
                }
                return;
            }
            const preferred = getPreferred();
            const obstacles = getVisibleOrbObstacleRects();
            const homeBlocked = preferredAreaIsBlocked(preferred, obstacles);
            if (homeBlocked) {
                clearReturnTimer();
                autoRelocatedRef.current =
                    true;
                applyPosition(getNearestSafeOrbPos(preferred, obstacles));
                return;
            }
            if (autoRelocatedRef.current) {
                scheduleReturnHome();
            }
            else if (!userPositionedRef.current) {
                applyPosition(preferred);
            }
        };
        const refreshResizeTargets = () => {
            if (!resizeObserver) {
                return;
            }
            resizeObserver.disconnect();
            getVisibleOrbObstacles()
                .forEach(element => {
                resizeObserver?.observe(element);
            });
            document
                .querySelectorAll(".holo-console, .chat-dock > *")
                .forEach(element => {
                resizeObserver?.observe(element);
            });
        };
        const scheduleEvaluation = () => {
            cancelAnimationFrame(evaluationRaf);
            evaluationRaf =
                requestAnimationFrame(() => {
                    refreshResizeTargets();
                    evaluateLayout();
                });
        };
        if (typeof ResizeObserver !==
            "undefined") {
            resizeObserver =
                new ResizeObserver(scheduleEvaluation);
        }
        const mutationObserver = new MutationObserver(scheduleEvaluation);
        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: [
                "class",
                "style",
                "data-status",
                "aria-hidden",
            ],
        });
        /*
         * Low-frequency geometry fallback for browser UI movement
         * that may not emit a reliable resize event.
         */
        evaluationInterval =
            window.setInterval(scheduleEvaluation, 320);
        window.addEventListener("resize", scheduleEvaluation);
        window.addEventListener("orientationchange", scheduleEvaluation);
        window.visualViewport?.addEventListener("resize", scheduleEvaluation);
        window.visualViewport?.addEventListener("scroll", scheduleEvaluation);
        window.addEventListener("ava-eye-toggle", scheduleEvaluation);
        scheduleEvaluation();
        return () => {
            cancelAnimationFrame(evaluationRaf);
            clearReturnTimer();
            window.clearInterval(evaluationInterval);
            mutationObserver.disconnect();
            resizeObserver?.disconnect();
            window.removeEventListener("resize", scheduleEvaluation);
            window.removeEventListener("orientationchange", scheduleEvaluation);
            window.visualViewport?.removeEventListener("resize", scheduleEvaluation);
            window.visualViewport?.removeEventListener("scroll", scheduleEvaluation);
            window.removeEventListener("ava-eye-toggle", scheduleEvaluation);
        };
    }, []);
    const closeMenu = () => {
        setOpen(false);
        setThemeOpen(false);
    };
    const startDrag = (clientX, clientY) => {
        if (visionClearTimerRef.current !== null) {
            window.clearTimeout(visionClearTimerRef.current);
            visionClearTimerRef.current = null;
        }
        autoRelocatedRef.current = false;
        dragRef.current.active = true;
        dragRef.current.moved = false;
        dragRef.current.startX = clientX;
        dragRef.current.startY = clientY;
        dragRef.current.offsetX = clientX - posRef.current.x;
        dragRef.current.offsetY = clientY - posRef.current.y;
        document.body.classList.add("ava-orb-dragging");
    };
    const moveDrag = (clientX, clientY) => {
        if (!dragRef.current.active)
            return;
        const dx = Math.abs(clientX - dragRef.current.startX);
        const dy = Math.abs(clientY - dragRef.current.startY);
        if (dx > 6 || dy > 6)
            dragRef.current.moved = true;
        const orbSize = 76;
        const margin = 12;
        const nextX = clamp(clientX - dragRef.current.offsetX, margin, Math.max(margin, window.innerWidth - orbSize - margin));
        const nextY = clamp(clientY - dragRef.current.offsetY, margin, Math.max(margin, window.innerHeight - orbSize - margin));
        // Instant drag tracking: no delayed catch-up.
        const next = {
            x: nextX,
            y: nextY,
        };
        posRef.current = next;
        setPos(next);
    };
    const endDrag = () => {
        if (!dragRef.current.active)
            return;
        dragRef.current.active = false;
        document.body.classList.remove("ava-orb-dragging");
        const preferred = normalizePos(posRef.current);
        userPositionedRef.current = true;
        preferredPosRef.current = preferred;
        autoRelocatedRef.current = false;
        posRef.current = preferred;
        setPos(preferred);
        savePos(preferred);
    };
    useEffect(() => {
        const onMouseMove = (e) => moveDrag(e.clientX, e.clientY);
        const onMouseUp = () => endDrag();
        const onTouchMove = (e) => {
            if (dragRef.current.active)
                e.preventDefault();
            const t = e.touches[0];
            if (!t)
                return;
            moveDrag(t.clientX, t.clientY);
        };
        const onTouchEnd = () => endDrag();
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        window.addEventListener("touchmove", onTouchMove, { passive: false });
        window.addEventListener("touchend", onTouchEnd);
        window.addEventListener("touchcancel", onTouchEnd);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            window.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("touchend", onTouchEnd);
            window.removeEventListener("touchcancel", onTouchEnd);
        };
    }, []);
    const handleOrbClick = () => {
        if (dragRef.current.moved)
            return;
        setOpen((prev) => {
            const next = !prev;
            if (!next)
                setThemeOpen(false);
            return next;
        });
    };
    const selectTheme = (nextTheme) => {
        setTheme(nextTheme);
        setThemeOpen(false);
        try {
            // === AVA LEGACY GLOBAL THEME CLEANUP ===
            // ThemeContext owns user-scoped theme storage. Remove old global keys
            // so one account cannot leak colors into another account.
            localStorage.removeItem("ava_theme");
            localStorage.removeItem("ava_theme_color");
            localStorage.removeItem("theme");
            window.dispatchEvent(new CustomEvent("ava-theme-change", {
                detail: { color: nextTheme },
            }));
            window.dispatchEvent(new Event("ava_interaction"));
        }
        catch (e) {
            console.warn("Theme change failed", e);
        }
    };
    const runOrbAction = (event, action) => {
        try {
            event?.preventDefault?.();
            event?.stopPropagation?.();
            clearInactiveTimer();
        }
        catch { }
        action();
    };
    const orbWrapRef = useRef(null);
    const inactiveTimerRef = useRef(null);
    const clearInactiveTimer = () => {
        if (inactiveTimerRef.current) {
            window.clearTimeout(inactiveTimerRef.current);
            inactiveTimerRef.current = null;
        }
    };
    const startInactiveTimer = () => {
        clearInactiveTimer();
        // Auto-close disabled:
        // Mobile orb now stays open until the user manually closes it.
        inactiveTimerRef.current = null;
    };
    useEffect(() => {
        if (!open) {
            clearInactiveTimer();
            return;
        }
        startInactiveTimer();
        const handlePointerOutside = (event) => {
            const target = event.target;
            if (!target)
                return;
            if (orbWrapRef.current && !orbWrapRef.current.contains(target)) {
                setOpen(false);
                setThemeOpen(false);
                clearInactiveTimer();
            }
        };
        const handleWindowBlur = () => {
            setOpen(false);
            setThemeOpen(false);
            clearInactiveTimer();
        };
        document.addEventListener("mousedown", handlePointerOutside);
        document.addEventListener("touchstart", handlePointerOutside, { passive: true });
        window.addEventListener("blur", handleWindowBlur);
        return () => {
            document.removeEventListener("mousedown", handlePointerOutside);
            document.removeEventListener("touchstart", handlePointerOutside);
            window.removeEventListener("blur", handleWindowBlur);
            clearInactiveTimer();
        };
    }, [open]);
    const menuStyle = {
        left: `${Math.max(8, pos.x - (isCompactLandscape ? 132 : isPhone ? 150 : 170))}px`,
        top: `${Math.max(8, pos.y - (isCompactLandscape
            ? (themeOpen ? 250 : 168)
            : themeOpen
                ? 340
                : 220))}px`,
        width: isCompactLandscape
            ? "min(78vw, 240px)"
            : isPhone
                ? "min(86vw, 290px)"
                : "min(92vw, 340px)",
        maxWidth: isCompactLandscape
            ? "240px"
            : isPhone
                ? "290px"
                : "340px",
        maxHeight: isCompactLandscape
            ? "58vh"
            : "70vh",
        transform: isCompactLandscape
            ? "scale(0.92)"
            : "scale(1)",
        transformOrigin: "bottom right",
        overflowY: "auto",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
    };
    return (_jsxs("div", { ref: orbWrapRef, onMouseEnter: clearInactiveTimer, onMouseLeave: () => {
            if (open)
                startInactiveTimer();
        }, onMouseMove: clearInactiveTimer, onTouchStart: clearInactiveTimer, onTouchEnd: () => {
            if (open)
                startInactiveTimer();
        }, children: [_jsx("div", { className: "mobile-orb-pulse", "aria-hidden": "true", style: {
                    left: `${pos.x}px`,
                    top: `${pos.y}px`,
                    right: "auto",
                    bottom: "auto",
                } }), _jsxs("button", { className: `mobile-orb
            ${orbSpeaking ? "orb-speaking" : ""}
            ${orbThinking ? "orb-thinking" : ""}
            ${orbListening ? "orb-listening" : ""}
          `, onClick: (e) => { clearInactiveTimer(); handleOrbClick(); }, onMouseDown: (e) => startDrag(e.clientX, e.clientY), onTouchStart: (e) => {
                    const t = e.touches[0];
                    if (!t)
                        return;
                    startDrag(t.clientX, t.clientY);
                }, "aria-label": interfaceCopy.mobile.openControls, type: "button", style: {
                    left: `${pos.x}px`,
                    top: `${pos.y}px`,
                    right: "auto",
                    bottom: "auto",
                }, children: [_jsx("div", { className: "orb-core" }), _jsx("div", { className: "orb-ring orb-ring-1" }), _jsx("div", { className: "orb-ring orb-ring-2" }), _jsx("div", { className: "orb-ring orb-ring-3" }), _jsxs("div", { className: "orb-particles", children: [_jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {})] }), _jsx("div", { className: "orb-center-glow" })] }), open && (_jsxs("div", { className: "mobile-orb-menu", onMouseEnter: clearInactiveTimer, onMouseMove: clearInactiveTimer, onTouchStart: clearInactiveTimer, role: "menu", "aria-label": interfaceCopy.mobile.controls, style: menuStyle, children: [_jsx("button", { type: "button", onClick: (e) => runOrbAction(e, goProfile), children: interfaceCopy.mobile.profile }), _jsx("button", { type: "button", onClick: () => {
                            window.dispatchEvent(new CustomEvent("ava-open-history"));
                            closeMenu();
                        }, children: interfaceCopy.mobile.history }), _jsx("button", { type: "button", onClick: () => {
                            if (isLockedFreeUser) {
                                lockNotice();
                                return;
                            }
                            window.location.href = "/settings";
                            closeMenu();
                        }, style: isLockedFreeUser ? { opacity: 0.45 } : undefined, children: interfaceCopy.mobile.system }), _jsx("button", { type: "button", onClick: (e) => runOrbAction(e, isFreePlanUser ? lockNotice : toggleOrbEye), "aria-pressed": orbEyeOpen, title: isFreePlanUser
                            ? interfaceCopy.mobile.visionLockedTitle
                            : orbEyeOpen
                                ? interfaceCopy.mobile.disableVision
                                : interfaceCopy.mobile.enableVision, className: "orb-menu-btn", style: isFreePlanUser ? { opacity: 0.55 } : isLockedFreeUser ? { opacity: 0.45 } : undefined, children: isFreePlanUser
                            ? interfaceCopy.mobile.visionLocked
                            : orbEyeOpen
                                ? interfaceCopy.mobile.visionOn
                                : interfaceCopy.mobile.visionOff }), _jsx("button", { type: "button", onClick: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            clearInactiveTimer();
                            if (!motionTrackingAllowed) {
                                window.alert(interfaceCopy.mobile.motionPaidAlert);
                                return;
                            }
                            void toggleOrbMotionTrack();
                        }, "aria-pressed": orbMotionEnabled, title: !motionTrackingAllowed
                            ? interfaceCopy.mobile.motionPaidTitle
                            : orbMotionEnabled
                                ? interfaceCopy.mobile.disableMotion
                                : interfaceCopy.mobile.enableMotion, className: "orb-menu-btn", style: !motionTrackingAllowed
                            ? { opacity: 0.55 }
                            : undefined, children: !motionTrackingAllowed
                            ? interfaceCopy.mobile.motionLocked
                            : orbMotionEnabled
                                ? interfaceCopy.mobile.motionOn
                                : interfaceCopy.mobile.motionOff }), _jsx("button", { type: "button", onClick: () => {
                            if (isLockedFreeUser) {
                                lockNotice();
                                return;
                            }
                            closeMenu();
                            setOpen(false);
                            setThemeOpen(false);
                            clearInactiveTimer();
                            // Smooth logout: let the orb menu close before dashboard redirects.
                            window.requestAnimationFrame(() => {
                                emit("ava-mobile-logout");
                            });
                        }, style: isLockedFreeUser ? { opacity: 0.45 } : undefined, children: interfaceCopy.mobile.logout })] }))] }));
}
